/**
 * lib/creditNotePdf.ts
 * Credit note PDF — references the credited invoice, lists the
 * credited lines and shows the total credited.
 */
import { jsPDF } from 'jspdf';
import type { CompanySettings, CreditNote } from '@/types';
import { formatPula } from './money';
import { drawBand, drawCompanyBlock, fmtDate, PDF_CHAR, PDF_GRAY, PDF_LINE, PAGE_W, MARGIN } from './pdfHeader';

export function creditNoteFileName(cn: CreditNote): string {
  return `CreditNote-${cn.number ?? 'draft'}.pdf`;
}

export function buildCreditNotePdf(cn: CreditNote, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = PAGE_W;
  const M = MARGIN;
  const right = W - M;
  const vatOn = cn.vat.registered;

  drawBand(doc, company, 'CREDIT NOTE');
  let y = drawCompanyBlock(doc, company, 48);

  let my = 48;
  const meta: Array<[string, string]> = [
    ['Credit note no.', cn.number ?? 'DRAFT'],
    ['Date', fmtDate(cn.issueDate)],
    ['Against invoice', cn.invoiceNumber],
  ];
  doc.setFontSize(11);
  meta.forEach(([k, v]) => {
    doc.setTextColor(...PDF_GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text(k, right - 66, my);
    doc.setTextColor(...PDF_CHAR);
    doc.setFont('helvetica', 'bold');
    doc.text(v, right, my, { align: 'right' });
    my += 5.5;
  });

  y = Math.max(y, my) + 6;
  doc.setTextColor(...PDF_GRAY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('CREDIT TO', M, y);
  y += 5.5;
  doc.setTextColor(...PDF_CHAR);
  doc.setFontSize(11.5);
  doc.text(cn.client.name, M, y);
  y += 5.5;

  // Table
  y += 6;
  const cDesc = M + 2;
  const cQty = right - 82;
  const cUnit = right - 42;
  const cAmt = right - 2;
  doc.setFillColor(255, 241, 234);
  doc.rect(M, y - 5, right - M, 9, 'F');
  doc.setTextColor(...PDF_CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('DESCRIPTION', cDesc, y);
  doc.text('QTY', cQty, y, { align: 'right' });
  doc.text('UNIT PRICE', cUnit, y, { align: 'right' });
  doc.text('AMOUNT', cAmt, y, { align: 'right' });
  y += 7;
  doc.setFont('helvetica', 'normal');
  cn.lineItems.forEach((l) => {
    const lines = doc.splitTextToSize(l.description, cQty - cDesc - 6);
    doc.text(lines, cDesc, y);
    doc.text(String(l.quantity), cQty, y, { align: 'right' });
    doc.text(formatPula(l.unitPrice), cUnit, y, { align: 'right' });
    doc.text(formatPula(l.lineTotal), cAmt, y, { align: 'right' });
    y += 5.5 * Math.max(1, lines.length) + 1.5;
    doc.setDrawColor(...PDF_LINE);
    doc.line(M, y - 3, right, y - 3);
  });

  // Totals
  y += 4;
  const lx = cUnit;
  const row = (a: string, b: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 13 : 11);
    doc.setTextColor(...(bold ? PDF_CHAR : PDF_GRAY));
    doc.text(a, lx, y, { align: 'right' });
    doc.setTextColor(...PDF_CHAR);
    doc.text(b, cAmt, y, { align: 'right' });
    y += bold ? 8 : 6;
  };
  row('Subtotal', formatPula(cn.totals.subtotal));
  if (vatOn) row(`VAT (${cn.vat.rate}%)`, formatPula(cn.totals.vatTotal));
  doc.setDrawColor(...PDF_CHAR);
  doc.line(lx - 42, y - 1, right, y - 1);
  y += 5;
  row('Total credited', formatPula(cn.totals.total), true);

  // Reason
  if (cn.reason) {
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...PDF_GRAY);
    doc.text('REASON', M, y);
    y += 5.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...PDF_CHAR);
    doc.splitTextToSize(cn.reason, right - M).forEach((l: string) => {
      doc.text(l, M, y);
      y += 5;
    });
  }

  if (!vatOn) {
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF_GRAY);
    doc.text('Not registered for VAT.', right, 290, { align: 'right' });
  }

  return doc;
}

export function downloadCreditNotePdf(cn: CreditNote, company: CompanySettings): void {
  buildCreditNotePdf(cn, company).save(creditNoteFileName(cn));
}

export function creditNotePdfBase64(cn: CreditNote, company: CompanySettings): string {
  return buildCreditNotePdf(cn, company).output('datauristring').split(',')[1] ?? '';
}
